import { Observable } from "rxjs";
import {
    SHIPPING_ACCOUNT_CARRIER_WAREHOUSE_SERVICES_COMPLETE,
    SHIPPING_ACCOUNT_CARRIER_WAREHOUSE_SERVICES_REQUEST,
    SHIPPING_ACCOUNT_CARRIER_WAREHOUSE_SERVICES_SET,
} from "constants/action-types";
import siteConfig from "site-config";
import { formatMessage } from "lib/globalize-util";
import { ajax } from "lib/observable";
import "rxjs/add/operator/map";
import { add as snackbarAdd } from "actions/snackbars";
import { getRecordsFromSchema, normalizeData } from "lib/api";
import { CarrierServiceWarehouseServices as CarrierServiceWarehouseServicesSchema } from "schemas/shipping-account/";
import { updateEntities } from "redux-query";
import forOwn from "lodash/forOwn";
import groupBy from "lodash/groupBy";

const getGroupedServices = (response) => {
    const groupedResponse = groupBy(response, "warehouse_id");
    const formattedWarehouseServices = [];

    forOwn(groupedResponse, (service) => {
        const serviceObject = {
            id: null,
            service_code: [],
            shipping_account_carrier_id: null,
            warehouse_id: null,
        };
        service.forEach((warehouseService: any) => {
            serviceObject.id = warehouseService.id; // we dont really care about ids for these objects
            serviceObject.service_code = warehouseService.service_code
                ? serviceObject.service_code.concat(warehouseService.service_code)
                : null;
            serviceObject.shipping_account_carrier_id = warehouseService.shipping_account_carrier_id;
            serviceObject.warehouse_id = warehouseService.warehouse_id;
        });
        formattedWarehouseServices.push(serviceObject);
    });

    return formattedWarehouseServices;
};

export const fetchAccountCarrierWarehouseServicesEpic = (action$, store) => {
    return action$
        .ofType(SHIPPING_ACCOUNT_CARRIER_WAREHOUSE_SERVICES_REQUEST)
        .map((action) => {
            return action.payload;
        })
        .concatMap((carrierId) => {
            return ajax({
                method: "GET",
                url: `${siteConfig.api}/api/v3.0/shipping/carrier/${carrierId}/warehouse-services`,
            })
                .map(({ response, status }) => {
                    if (response && status === 200) {
                        const formattedWarehouseServices = getGroupedServices(response);
                        const warehouseServiceRecords = getRecordsFromSchema(CarrierServiceWarehouseServicesSchema);
                        const data = normalizeData(
                            formattedWarehouseServices,
                            CarrierServiceWarehouseServicesSchema,
                            warehouseServiceRecords
                        );
                        return { entities: data.entities, result: data.result, error: false };
                    } else {
                        return { error: true, entities: null, result: [] };
                    }
                })
                .flatMap(({ entities, result, error }) => {
                    if (error) {
                        return [
                            store.dispatch(snackbarAdd({ message: formatMessage("Error loading warehouse services") })),
                            {
                                payload: [],
                                error: true,
                                type: SHIPPING_ACCOUNT_CARRIER_WAREHOUSE_SERVICES_COMPLETE,
                            },
                        ];
                    } else {
                        return [
                            updateEntities({
                                accountCarrierServiceWarehouseService: (accountCarrierServiceWarehouseService = {}) => {
                                    return {
                                        ...entities.accountCarrierServiceWarehouseService,
                                    };
                                },
                            }),
                            {
                                payload: result,
                                type: SHIPPING_ACCOUNT_CARRIER_WAREHOUSE_SERVICES_COMPLETE,
                            },
                        ];
                    }
                })
                .catch((error) => {
                    let errorMessage = formatMessage("Error loading warehouse services");
                    if (error.status === 401) {
                        errorMessage = formatMessage("You do not have permission to view warehouse services");
                    }
                    return Observable.merge(
                        Observable.of({
                            error: true,
                            payload: new Error(errorMessage),
                            type: SHIPPING_ACCOUNT_CARRIER_WAREHOUSE_SERVICES_COMPLETE,
                        }),
                        Observable.of(snackbarAdd({ message: errorMessage }))
                    );
                });
        });
};

export const setAccountCarrierWarehouseServicesEpic = (action$, store) => {
    return action$
        .ofType(SHIPPING_ACCOUNT_CARRIER_WAREHOUSE_SERVICES_SET)
        .map((action) => {
            return action.payload;
        })
        .concatMap(({ services, carrier }) => {
            return ajax({
                method: "PUT",
                body: {
                    ...services,
                },
                url: `${siteConfig.api}/api/v3.0/shipping/carrier/${carrier}/warehouse-services`,
            })
                .map(({ response, status }) => {
                    if (response && [202, 201].includes(status)) {
                        const formattedWarehouseServices = getGroupedServices(response);
                        const warehouseServiceRecords = getRecordsFromSchema(CarrierServiceWarehouseServicesSchema);
                        const data = normalizeData(
                            formattedWarehouseServices,
                            CarrierServiceWarehouseServicesSchema,
                            warehouseServiceRecords
                        );
                        return { entities: data.entities, result: data.result, error: false };
                    } else {
                        return { error: true, entities: null, result: [] };
                    }
                })
                .flatMap(({ entities, result, error }) => {
                    if (error) {
                        return [
                            store.dispatch(snackbarAdd({ message: formatMessage("Error saving warehouse services") })),
                            {
                                payload: [],
                                error: true,
                                type: SHIPPING_ACCOUNT_CARRIER_WAREHOUSE_SERVICES_COMPLETE,
                            },
                        ];
                    } else {
                        return [
                            updateEntities({
                                accountCarrierServiceWarehouseService: (accountCarrierServiceWarehouseService = {}) => {
                                    return {
                                        ...entities.accountCarrierServiceWarehouseService,
                                    };
                                },
                            }),
                            {
                                payload: result,
                                type: SHIPPING_ACCOUNT_CARRIER_WAREHOUSE_SERVICES_COMPLETE,
                            },
                            snackbarAdd({ message: formatMessage("Warehouse services updated") }),
                        ];
                    }
                })
                .catch((error) => {
                    let errorMessage = formatMessage("Error saving warehouse services");
                    if (error.status === 401) {
                        errorMessage = formatMessage("You do not have permission to edit warehouse services");
                    }
                    return Observable.merge(
                        Observable.of({
                            error: true,
                            payload: new Error(errorMessage),
                            type: SHIPPING_ACCOUNT_CARRIER_WAREHOUSE_SERVICES_COMPLETE,
                        }),
                        Observable.of(snackbarAdd({ message: errorMessage }))
                    );
                });
        });
};
